import type { ReactNode } from 'react';
import { GRID_SIZE } from '@bn/shared';
import { COLUMNS, ROWS, cellLabel } from './helpers';

export interface CellRender {
  className: string;
  content?: ReactNode;
  /** Texte lu par les lecteurs d'écran et affiché en infobulle. */
  description: string;
  disabled?: boolean;
}

interface BoardProps {
  renderCell: (x: number, y: number) => CellRender;
  onCellClick?: (x: number, y: number) => void;
  onCellEnter?: (x: number, y: number) => void;
  onLeave?: () => void;
  /** Estompe la grille (tour adverse, partie terminée…). */
  dimmed?: boolean;
}

export function Board({
  renderCell,
  onCellClick,
  onCellEnter,
  onLeave,
  dimmed = false,
}: BoardProps) {
  return (
    <div
      className={`grid select-none gap-0.5 transition-opacity ${dimmed ? 'opacity-60' : ''}`}
      style={{ gridTemplateColumns: `1.5rem repeat(${GRID_SIZE}, minmax(0, 1fr))` }}
      onMouseLeave={onLeave}
    >
      <span />
      {COLUMNS.map((label) => (
        <span key={label} className="text-center text-xs font-semibold text-slate-400">
          {label}
        </span>
      ))}

      {ROWS.map((row, y) => (
        <ItemRow
          key={row}
          row={row}
          y={y}
          renderCell={renderCell}
          onCellClick={onCellClick}
          onCellEnter={onCellEnter}
        />
      ))}
    </div>
  );
}

function ItemRow({
  row,
  y,
  renderCell,
  onCellClick,
  onCellEnter,
}: {
  row: number;
  y: number;
  renderCell: BoardProps['renderCell'];
  onCellClick?: BoardProps['onCellClick'];
  onCellEnter?: BoardProps['onCellEnter'];
}) {
  return (
    <>
      <span className="flex items-center justify-center text-xs font-semibold text-slate-400">
        {row}
      </span>
      {COLUMNS.map((_, x) => {
        const cell = renderCell(x, y);
        return (
          <button
            key={x}
            type="button"
            disabled={cell.disabled ?? !onCellClick}
            title={cell.description}
            aria-label={`${cellLabel(x, y)} — ${cell.description}`}
            onClick={() => onCellClick?.(x, y)}
            onMouseEnter={() => onCellEnter?.(x, y)}
            className={`cellule flex items-center justify-center rounded-[3px] text-xs font-bold transition ${cell.className}`}
          >
            {cell.content}
          </button>
        );
      })}
    </>
  );
}
